import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Award, BarChart3, Settings, IdCard } from 'lucide-react';
import PlayerCard from '../components/PlayerCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.includes('/api/files/')) {
    const path = url.substring(url.indexOf('/api/files/'));
    return `${BACKEND_URL}${path}`;
  }
  return url;
};

const POSITION_LABELS = {
  linha: '⚽ Linha',
  defesa: '🛡️ Defesa',
  guarda_redes: '🧤 Guarda-Redes',
};

const PlayerProfilePage = () => {
  const { name } = useParams();
  const decodedName = decodeURIComponent(name || '');
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    fetchPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedName]);

  const fetchPlayer = async () => {
    try {
      const res = await axios.get(`${API}/stats/all-time`);
      const found = (res.data?.players || []).find(p => p.name === decodedName);
      if (!found) {
        setError('Jogador não encontrado');
      } else {
        setPlayer(found);
      }
    } catch (e) {
      setError('Erro ao carregar dados');
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando...</div>;
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-zinc-50 p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border-2 border-zinc-200 p-12 text-center">
          <p className="text-zinc-600 mb-4">{error || 'Jogador não encontrado'}</p>
          <a href="/estatisticas" className="btn-secondary inline-block" data-testid="back-stats">← Voltar às Estatísticas</a>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl(player.highlight_image_url || player.profile_image_url);
  const titles = player.titles || [];

  const Stat = ({ label, value, color = 'text-zinc-800' }) => (
    <div className="bg-white border-2 border-zinc-200 rounded-lg p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</p>
      <p className={`text-2xl font-black ${color}`}>{value}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="player-profile">
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="text-2xl sm:text-3xl font-black tracking-tighter hover:text-green-400 transition-colors">⚽ DOMINGÃO</a>
          <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors"><Settings size={24} /></a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <a href="/estatisticas" className="text-sm underline mb-4 inline-flex items-center gap-1 text-zinc-600" data-testid="back-link">
          <ArrowLeft size={14} /> Voltar
        </a>

        {/* Hero */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-700 rounded-2xl p-6 sm:p-8 text-white mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {imageUrl ? (
              <img src={imageUrl} alt={player.name} className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover border-4 border-white/30 shadow-xl" />
            ) : (
              <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-white/20 flex items-center justify-center text-6xl font-black border-4 border-white/30">
                {player.name.charAt(0)}
              </div>
            )}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-2" data-testid="player-name">{player.name}</h1>
              <span className="inline-block bg-white/20 px-3 py-1 rounded-full text-sm font-bold mb-3">
                {POSITION_LABELS[player.position] || POSITION_LABELS.linha}
              </span>
              <div className="flex gap-4 justify-center sm:justify-start flex-wrap">
                <div className="bg-yellow-500 text-zinc-900 px-4 py-2 rounded-lg">
                  <p className="text-[10px] uppercase font-black opacity-70">Pontos_Rating</p>
                  <p className="text-2xl font-black" data-testid="player-pontos">{(player.pontos_rating || 0).toFixed(1)}</p>
                </div>
                <div className="bg-green-500 text-white px-4 py-2 rounded-lg">
                  <p className="text-[10px] uppercase font-black opacity-70">Golos</p>
                  <p className="text-2xl font-black">{player.goals || 0}</p>
                </div>
                <div className="bg-blue-500 text-white px-4 py-2 rounded-lg">
                  <p className="text-[10px] uppercase font-black opacity-70">Assistências</p>
                  <p className="text-2xl font-black">{player.assists || 0}</p>
                </div>
                <button
                  onClick={() => setShowCard(true)}
                  className="bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-zinc-900 px-4 py-2 rounded-lg font-black flex items-center gap-2 transition-all shadow-lg hover:scale-105"
                  data-testid="open-card-from-profile"
                >
                  <IdCard size={20} strokeWidth={2.5} />
                  <span>VER CARTA</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Títulos */}
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 mb-6">
          <h2 className="text-lg font-black uppercase mb-3 flex items-center gap-2 text-yellow-700">
            <Award size={20} /> Títulos
          </h2>
          {titles.length === 0 ? (
            <p className="text-zinc-400 text-sm italic">Ainda sem títulos. Continua a jogar!</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="player-titles">
              {titles.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1.5 rounded-full border border-yellow-300">
                  <Award size={12} /> {t}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Ataque */}
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 mb-4">
          <h2 className="text-lg font-black uppercase mb-3 text-green-700">⚽ Ataque</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Golos" value={player.goals || 0} color="text-green-700" />
            <Stat label="Assist." value={player.assists || 0} color="text-blue-700" />
            <Stat label="G + A" value={player.goal_participation || 0} color="text-orange-700" />
            <Stat label="Fintas" value={player.dribbles || 0} />
            <Stat label="Remates Baliza" value={player.shots_on_target || 0} />
            <Stat label="Remates Fora" value={player.shots_missed || 0} />
            <Stat label="% Remate" value={`${player.shot_accuracy || 0}%`} color="text-green-700" />
            <Stat label="Lançamentos" value={player.throws || 0} />
          </div>
        </section>

        {/* Distribuição */}
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 mb-4">
          <h2 className="text-lg font-black uppercase mb-3 text-blue-700">📤 Distribuição</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Passes Certos" value={player.passes_completed || 0} />
            <Stat label="Passes Errados" value={player.passes_failed || 0} />
            <Stat label="% Passe" value={`${player.pass_accuracy || 0}%`} color="text-blue-700" />
            <Stat label="Perda Posse" value={player.possession_lost || 0} />
          </div>
        </section>

        {/* Defesa */}
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 mb-4">
          <h2 className="text-lg font-black uppercase mb-3 text-purple-700">🛡️ Defesa</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Desarmes" value={player.tackles || 0} />
            <Stat label="Bloqueios" value={player.blocks || 0} />
            <Stat label="Interceções" value={player.interceptions || 0} />
            <Stat label="JSG" value={player.clean_sheets || 0} />
            {player.position === 'guarda_redes' && (
              <>
                <Stat label="Defesas" value={player.saves || 0} />
                <Stat label="Golos Sofridos" value={player.goals_conceded || 0} color="text-red-600" />
              </>
            )}
          </div>
        </section>

        {/* Sessão */}
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 mb-4">
          <h2 className="text-lg font-black uppercase mb-3 text-yellow-700">🏆 Resultados &amp; Presença</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Vitórias" value={player.wins || 0} color="text-green-700" />
            <Stat label="Empates" value={player.draws || 0} />
            <Stat label="Jogos" value={player.games_played || 0} color="text-purple-700" />
          </div>
        </section>

        {/* Erros */}
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 mb-6">
          <h2 className="text-lg font-black uppercase mb-3 text-red-700">⚠️ Erros</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Faltas Comet." value={player.fouls_committed || 0} />
            <Stat label="Faltas Sofridas" value={player.fouls_suffered || 0} />
            <Stat label="Golo Contra" value={player.own_goals || 0} color="text-red-600" />
            <Stat label="Erro Domínio" value={player.control_errors || 0} />
          </div>
        </section>

        <div className="text-center">
          <a href="/estatisticas" className="btn-secondary inline-flex items-center gap-2" data-testid="link-central">
            <BarChart3 size={16} /> Ver Central de Estatísticas
          </a>
        </div>
      </main>
      {showCard && <PlayerCard playerName={player.name} onClose={() => setShowCard(false)} />}
    </div>
  );
};

export default PlayerProfilePage;
